"use client"

import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { X, FileUp } from "lucide-react"
import { uploadImageAction } from "../../../services/action/common"
import { updateLeadsActionManager } from "../../../services/action/manager"

const SolutionUploadModal = ({ setShowSolutionUpload, leadDetails }) => {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.authenticationReducer)
  const [solutionFiles, setSolutionFiles] = useState([])
  const [verifications, setVerifications] = useState({
    aiVerified: false,
    plagiarismCheck: false,
    grammarCheck: false,
    wordCountRequirement: false,
    rubricFollowed: false,
  })
  const [isUploading, setIsUploading] = useState(false)
  const [errors, setErrors] = useState({})
  const [isPartialDelivery, setIsPartialDelivery] = useState(false)

  const handleSolutionUpload = async (e) => {
    const files = Array.from(e.target.files)
    setIsUploading(true)
    setErrors({})

    try {
      for (const file of files) {
        const formData = new FormData()
        formData.append("images", file)

        const response = await new Promise((resolve) => {
          dispatch(
            uploadImageAction({
              data: formData,
              cb: resolve,
            }),
          )
        })

        if (response?.imageUrls) {
          setSolutionFiles((prev) => [
            ...prev,
            ...response.imageUrls.map((url) => ({
              url,
              name: file.name,
            })),
          ])
        }
      }
    } catch (error) {
      console.error("Error uploading file:", error)
      setErrors((prev) => ({ ...prev, upload: "Failed to upload files. Please try again." }))
    } finally {
      setIsUploading(false)
    }
  }

  const toggleVerification = (key) => {
    setVerifications((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const validateForm = () => {
    const newErrors = {}
    if (solutionFiles.length === 0) {
      newErrors.files = "Please upload at least one file."
    }
    if (!Object.values(verifications).some((v) => v)) {
      newErrors.verifications = "Please mark at least one checkbox"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSolutionSubmit = () => {
    if (!validateForm()) {
      return
    }

    const solutionData = {
      leadId: leadDetails?._id,
      imageUrls: solutionFiles,
      verifications,
      isPartialDelivery,
    }

    dispatch(
      updateLeadsActionManager({
        userType: user?.userType,
        data: solutionData,
        cb: () => {
          setShowSolutionUpload(false)
        },
      }),
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-[500px] max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-[#00234a]">Upload Solution</h3>
          <button
            onClick={() => setShowSolutionUpload(false)}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="space-y-4">
          {errors.submit && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
              <span className="block sm:inline">{errors.submit}</span>
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Verification Checklist</label>
            <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
              {Object.entries(verifications).map(([key, value]) => (
                <label key={key} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={() => toggleVerification(key)}
                    className="rounded border-gray-300 text-[#00234a] focus:ring-[#00234a]"
                  />
                  <span className="text-sm text-gray-600">
                    {key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                  </span>
                </label>
              ))}
            </div>
            {errors.verifications && <p className="text-red-500 text-xs mt-1">{errors.verifications}</p>}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Upload Files <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="file"
                onChange={handleSolutionUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                multiple
                disabled={isUploading}
              />
              <div
                className={`border-2 border-dashed rounded-lg p-4 text-center hover:bg-gray-50 ${
                  errors.files ? "border-red-500" : "border-gray-300"
                }`}
              >
                <FileUp className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-600">
                  {isUploading ? "Uploading..." : "Click to upload or drag and drop"}
                </p>
                {solutionFiles.length > 0 && (
                  <p className="text-xs text-gray-500 mt-2">{solutionFiles.length} files selected</p>
                )}
              </div>
            </div>
            {errors.files && <p className="text-red-500 text-xs mt-1">{errors.files}</p>}
          </div>

          {solutionFiles.length > 0 && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Uploaded Files</label>
              <div className="max-h-40 overflow-y-auto space-y-2">
                {solutionFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm text-gray-600 truncate">{file.name}</span>
                    <button
                      onClick={() => setSolutionFiles((files) => files.filter((_, i) => i !== index))}
                      className="p-1 hover:bg-gray-200 rounded-full"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="partial-delivery"
              checked={isPartialDelivery}
              onChange={(e) => setIsPartialDelivery(e.target.checked)}
              className="rounded border-gray-300 text-[#00234a] focus:ring-[#00234a]"
            />
            <label htmlFor="partial-delivery" className="text-sm text-gray-700">
              Partial Delivery
            </label>
          </div>

          <button
            onClick={handleSolutionSubmit}
            disabled={isUploading}
            className={`w-full py-3 rounded-lg font-medium transition-colors ${
              isUploading ? "bg-gray-400 text-white cursor-not-allowed" : "bg-[#00234a] text-white hover:bg-[#001a38]"
            }`}
          >
            {isUploading ? "Uploading..." : "SUBMIT"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default SolutionUploadModal

